import React from 'react';
import { User } from 'lucide-react';
import { cn } from '../../utils/cn';

export interface UserAvatarProps {
  name?: string | null;
  size?: 'xs' | 'sm' | 'md' | 'lg' | 'xl';
  className?: string;
  showIconIfEmpty?: boolean;
}

const sizeClasses = {
  xs: 'h-7 w-7 text-[10px]',
  sm: 'h-8 w-8 sm:h-10 sm:w-10 text-xs',
  md: 'h-10 w-10 text-sm',
  lg: 'h-12 w-12 text-base',
  xl: 'h-24 w-24 text-2xl',
};

const iconSizes = {
  xs: 'h-3.5 w-3.5',
  sm: 'h-4 w-4 sm:h-5 sm:w-5',
  md: 'h-5 w-5',
  lg: 'h-6 w-6',
  xl: 'h-12 w-12',
};

function initialsFromName(name: string): string {
  const parts = name.trim().split(/\s+/).filter(Boolean);
  if (parts.length === 0) return '?';
  if (parts.length === 1) return parts[0]!.slice(0, 2).toUpperCase();
  return `${parts[0]![0] ?? ''}${parts[parts.length - 1]![0] ?? ''}`.toUpperCase();
}

/**
 * Solid-color user avatar (no gradients). Matches primary app button styling.
 */
const UserAvatar: React.FC<UserAvatarProps> = ({
  name,
  size = 'md',
  className,
  showIconIfEmpty = true,
}) => {
  const label = name?.trim();
  const initials = label ? initialsFromName(label) : null;

  return (
    <div
      className={cn(
        'flex shrink-0 items-center justify-center rounded-full bg-blue-600 font-semibold text-white shadow-sm ring-2 ring-white',
        sizeClasses[size],
        className
      )}
      aria-hidden={!label}
    >
      {initials ? (
        <span>{initials}</span>
      ) : showIconIfEmpty ? (
        <User className={iconSizes[size]} aria-hidden />
      ) : (
        <span>?</span>
      )}
    </div>
  );
};

export default React.memo(UserAvatar);
